import { useState, useEffect } from 'react';
import { regionService } from '../services/regionMunicipalitiesTowns';
import { useTranslation } from 'react-i18next';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export const useRegions = () => {
    const { t } = useTranslation();
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedMunicipality, setSelectedMunicipality] = useState(null);
    const [selectedTown, setSelectedTown] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [isCountriesLoaded, setIsCountriesLoaded] = useState(false);

    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const [municipalities, setMunicipalities] = useState([]);
    const [towns, setTowns] = useState([]);

    const [modalState, setModalState] = useState({
        visible: false,
        type: null,
        mode: null,
        editData: null
    });

    const [deleteModalState, setDeleteModalState] = useState({
        visible: false,
        type: null,
        data: null
    });

    useEffect(() => {
        const initializeWithBulgaria = async () => {
            try {
                const data = await regionService.getCountries();
                setCountries(data);
                setIsCountriesLoaded(true);

                const bulgaria = data.find(country => country.countryCode === 'BG');
                if (bulgaria) {
                    const bgOption = {
                        value: bulgaria.countryCode,
                        label: bulgaria.countryNameBg
                    };
                    setSelectedCountry(bgOption);
                    const regionsData = await regionService.getRegions('BG');
                    setRegions(regionsData);
                }
            } catch (error) {
                console.error('Error initializing with Bulgaria:', error);
            }
        };

        initializeWithBulgaria();
    }, []);

    const fetchCountries = async () => {
        if (!isCountriesLoaded) {
            try {
                const data = await regionService.getCountries();
                setCountries(data);
                setIsCountriesLoaded(true);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        }
    };

    const fetchRegions = async (countryCode) => {
        try {
            const data = await regionService.getRegions(countryCode);
            setRegions(data);
        } catch (error) {
            console.error('Error fetching regions:', error);
            setRegions([]);
        }
    };

    const fetchMunicipalities = async (regionId) => {
        try {
            const data = await regionService.getMunicipalities(regionId);
            setMunicipalities(data);
        } catch (error) {
            console.error('Error fetching municipalities:', error);
            setMunicipalities([]);
        }
    };

    const fetchTowns = async (municipalityId) => {
        try {
            const data = await regionService.getTowns(municipalityId);
            setTowns(data);
        } catch (error) {
            console.error('Error fetching towns:', error);
            setTowns([]);
        }
    };

    const handleCountryChange = (selected) => {
        setSelectedCountry(selected);
        setSelectedRegion(null);
        setSelectedMunicipality(null);
        setSelectedTown(null);
        setMunicipalities([]);
        setTowns([]);
        
        if (selected) {
            fetchRegions(selected.value);
        } else {
            setRegions([]);
        }
    };

    const handleRegionSelect = (region) => {
        setSelectedRegion(region);
        setSelectedMunicipality(null);
        setSelectedTown(null);
        setTowns([]);
        
        if (region) {
            fetchMunicipalities(region.id);
        } else {
            setMunicipalities([]);
        }
    };

    const handleMunicipalitySelect = (municipality) => {
        setSelectedMunicipality(municipality);
        setSelectedTown(null);
        
        if (municipality) {
            fetchTowns(municipality.id);
        } else {
            setTowns([]);
        }
    };

    const handleTownSelect = (town) => {
        setSelectedTown(town);
    };

    const handleOpenModal = async (type, mode, data = null) => {
        if (type === 'town' && mode === 'edit' && data) {
            try {
                const townData = await regionService.getTownById(data.id);
                data = townData;
            } catch (error) {
                console.error('Error fetching town details:', error);
            }
        }

        setModalState(prevState => ({
            ...prevState,
            visible: true,
            type,
            mode,
            editData: data
        }));
    };

    const handleCloseModal = () => {
        setModalState(prevState => ({
            ...prevState,
            visible: false,
            type: null,
            mode: null,
            editData: null
        }));
    };

    const handleConfirm = async (formData) => {
        try {
            if (modalState.type === 'country') {
                if (modalState.mode === 'add') {
                    await regionService.createCountry(formData);
                    notifySuccess(t('regions.modal.countryCreated'));
                } else {
                    await regionService.updateCountry(modalState.editData.countryCode, {
                        ...formData,
                        active: true
                    });
                    notifySuccess(t('regions.modal.countryUpdated'));
                    if (selectedCountry && selectedCountry.value === modalState.editData.countryCode) {
                        setSelectedCountry({
                            value: formData.countryCode,
                            label: formData.countryNameBg
                        });
                    }
                    setCountries(prevCountries => 
                        prevCountries.map(country => 
                            country.countryCode === modalState.editData.countryCode
                                ? { ...country, ...formData }
                                : country
                        )
                    );
                }
                setIsCountriesLoaded(false);
                await fetchCountries();
                handleCloseModal();
            } else if (modalState.type === 'region') {
                if (modalState.mode === 'add') {
                    await regionService.createRegion({
                        name: formData.name,
                        description: formData.name,
                        countryCode: selectedCountry.value,
                        active: true
                    });
                    notifySuccess(t('regions.modal.regionCreated'));
                } else {
                    await regionService.updateRegion(modalState.editData.id, {
                        name: formData.name,
                        description: formData.name,
                        countryCode: selectedCountry.value,
                        active: true
                    });
                    notifySuccess(t('regions.modal.regionUpdated'));
                    if (selectedRegion && selectedRegion.id === modalState.editData.id) {
                        setSelectedRegion({
                            ...selectedRegion,
                            name: formData.name
                        });
                    }
                }
                if (selectedCountry) {
                    await fetchRegions(selectedCountry.value);
                }
                handleCloseModal();
            } else if (modalState.type === 'municipality') {
                if (modalState.mode === 'add') {
                    await regionService.createMunicipality({
                        name: formData.name,
                        regionId: selectedRegion.id,
                        countryCode: selectedCountry.value
                    });
                    notifySuccess(t('regions.modal.municipalityCreated'));
                } else {
                    await regionService.updateMunicipality(modalState.editData.id, {
                        name: formData.name,
                        regionId: selectedRegion.id,
                        countryCode: selectedCountry.value
                    });
                    notifySuccess(t('regions.modal.municipalityUpdated'));
                    if (selectedMunicipality && selectedMunicipality.id === modalState.editData.id) {
                        setSelectedMunicipality({
                            ...selectedMunicipality,
                            name: formData.name
                        });
                    }
                }
                if (selectedRegion) {
                    await fetchMunicipalities(selectedRegion.id);
                }
                handleCloseModal();
            } else if (modalState.type === 'town') {
                if (modalState.mode === 'add') {
                    try {
                        await regionService.createTown({
                            ...formData,
                            municipalityId: selectedMunicipality.id,
                            regionId: selectedRegion.id
                        });
                        notifySuccess(t('regions.modal.townCreated'));
                        if (selectedMunicipality) {
                            await fetchTowns(selectedMunicipality.id);
                        }
                        handleCloseModal();
                    } catch (error) {
                        notifyError(error.response?.data?.ekatte || t('common.error.errorFetchingData'));
                    }
                } else {
                    try {
                        await regionService.updateTown(modalState.editData.id, {
                            ...formData,
                            municipalityId: selectedMunicipality.id,
                            regionId: selectedRegion.id,
                            cityTypeId: parseInt(formData.cityTypeId || formData.type, 10)
                        });
                        notifySuccess(t('regions.modal.townUpdated'));
                        if (selectedTown && selectedTown.id === modalState.editData.id) {
                            setSelectedTown({
                                ...selectedTown,
                                name: formData.name,
                                postal: formData.postal,
                                ekatte: formData.ekatte,
                                cityType: formData.type,
                                transliteration: formData.transliteration
                            });
                        }
                        if (selectedMunicipality) {
                            await fetchTowns(selectedMunicipality.id);
                        }
                        handleCloseModal();
                    } catch (error) {
                        notifyError(error.response?.data?.ekatte || t('common.error.errorFetchingData'));
                    }
                }
            }
        } catch (error) {
            console.error('Error details:', error.response?.data);
            console.error('Error saving data:', error);
        }
    };

    const handleOpenDeleteModal = async (type, data) => {
        try {
            let entityData = data;
            let entityName = '';

            switch (type) {
                case 'country':
                    entityName = entityData.countryNameBg;
                    break;
                case 'region':
                case 'municipality':
                    entityName = entityData.name;
                    break;
                case 'town':
                    const [name] = entityData.name.split(' / ');
                    entityName = name;
                    break;
            }

            if (!entityName) {
                console.error('Failed to get entity name for deletion');
                return;
            }

            setDeleteModalState({
                visible: true,
                type,
                data: {
                    ...entityData,
                    name: entityName
                }
            });
        } catch (error) {
            console.error('Error opening delete modal:', error);
        }
    };

    const handleCloseDeleteModal = () => {
        setDeleteModalState({
            visible: false,
            type: null,
            data: null
        });
    };

    const handleConfirmDelete = async () => {
        try {
            if (deleteModalState.type === 'country') {
                await regionService.deleteCountry(deleteModalState.data.countryCode);
                setSelectedCountry(null);
                setIsCountriesLoaded(false);
                await fetchCountries();
                notifySuccess(t('regions.modal.countryDeleted'));
            } else if (deleteModalState.type === 'region') {
                await regionService.deleteRegion(deleteModalState.data.id);
                setSelectedRegion(null);
                if (selectedCountry) {
                    await fetchRegions(selectedCountry.value);
                }
                notifySuccess(t('regions.modal.regionDeleted'));
            } else if (deleteModalState.type === 'municipality') {
                await regionService.deleteMunicipality(deleteModalState.data.id);
                setSelectedMunicipality(null);
                if (selectedRegion) {
                    await fetchMunicipalities(selectedRegion.id);
                }
                notifySuccess(t('regions.modal.municipalityDeleted'));
            } else if (deleteModalState.type === 'town') {
                await regionService.deleteTown(deleteModalState.data.id);
                setSelectedTown(null);
                if (selectedMunicipality) {
                    await fetchTowns(selectedMunicipality.id);
                }
                notifySuccess(t('regions.modal.townDeleted'));
            }
        } catch (error) {
            console.error('Error deleting:', error);
        }
        handleCloseDeleteModal();
    };

    return {
        // State
        selectedRegion,
        selectedMunicipality,
        selectedTown,
        selectedCountry,
        countries,
        regions,
        municipalities,
        towns,
        modalState,
        deleteModalState,
        
        // Methods
        handleCountryChange,
        handleRegionSelect,
        handleMunicipalitySelect,
        handleTownSelect,
        handleOpenModal,
        handleCloseModal,
        handleConfirm,
        handleOpenDeleteModal,
        handleCloseDeleteModal,
        handleConfirmDelete,
        fetchCountries
    };
}; 